import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { Platform } from 'react-native'

interface CreateAlbumData {
  title: string
  description?: string
  uris: string[]
  sharedWithAccountIds?: string[]
}

// Mirrors the data-URL → Blob decoder in useCreateMemory.ts. expo-image-picker
// returns `data:image/jpeg;base64,...` on web; fetching that URL is flaky
// across browsers / CSP, so decode the base64 payload directly.
const dataUrlToBlob = (dataUrl: string): Blob => {
  const commaIdx = dataUrl.indexOf(',')
  if (!dataUrl.startsWith('data:') || commaIdx === -1) {
    throw new Error('Expected a data URL from the image picker')
  }
  const meta = dataUrl.slice(5, commaIdx)
  const payload = dataUrl.slice(commaIdx + 1)
  const isBase64 = meta.endsWith(';base64')
  const mime = (isBase64 ? meta.slice(0, -7) : meta) || 'image/jpeg'

  if (!isBase64) {
    return new Blob([decodeURIComponent(payload)], { type: mime })
  }
  const bytes = atob(payload)
  const buffer = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i)
  return new Blob([buffer], { type: mime })
}

const createAlbum = async (data: CreateAlbumData): Promise<string> => {
  const formData = new FormData()

  formData.append('title', data.title)
  if (data.description) formData.append('description', data.description)

  const validIds = (data.sharedWithAccountIds ?? []).filter(
    (id): id is string => typeof id === 'string' && id.trim() !== '',
  )
  validIds.forEach((id) => formData.append('sharedWithAccountIds', id))

  for (const uri of data.uris) {
    if (Platform.OS === 'web') {
      formData.append('files', dataUrlToBlob(uri), 'memory.jpg')
    } else {
      formData.append('files', {
        uri,
        type: 'image/jpeg',
        name: 'memory.jpg',
      } as any)
    }
  }

  const { data: responseData } = await api.post<string>('/albums', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return responseData
}

export const useCreateAlbum = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<string, Error, CreateAlbumData>({
    mutationFn: createAlbum,
    onSuccess: async () => {
      showSuccessToast(t('toast.success'), t('toast.albumCreated'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['albums'] }),
        queryClient.invalidateQueries({ queryKey: ['memories'] }),
        queryClient.invalidateQueries({ queryKey: ['myMemories'] }),
        queryClient.invalidateQueries({ queryKey: ['contact-memories'] }),
      ])
    },
    onError: (error) => {
      showApiErrorToast(t, error)
    },
  })
}
